import { Routes } from '@angular/router';
import {
  canActivate,
  redirectLoggedInTo,
  redirectUnauthorizedTo,
} from '@angular/fire/auth-guard';
import { LoginPage } from './pages/auth/login/login.page';
import { hasCustomClaimRedirect } from './util/AuthGuard';
import { AdminHomePage } from './pages/admin-home/admin-home.page';

const redirectLoggedInToDashboard = () => redirectLoggedInTo(['/dashboard']);
const redirectUnauthorizedToLogin = () =>
  redirectUnauthorizedTo(['/auth/login']);

const isOnboardedUser = () => hasCustomClaimRedirect('user', '/onboarding');
const isVerifiedUser = () => hasCustomClaimRedirect('verified', '/dashboard');
const isAdmin = () => hasCustomClaimRedirect('central_admin', '/not-authorized');

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        component: LoginPage,
        ...canActivate(redirectLoggedInToDashboard),
      },
    ],
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./pages/admin.page').then(m => m.AdminPage),
    ...canActivate(redirectUnauthorizedToLogin),
    ...canActivate(isAdmin),
    children: [
      {
        path: '',
        title: 'Administration',
        component: AdminHomePage,
      },

      {
        path: 'elearning',
        title: 'E-Learning Verwaltung',
        loadComponent: () => import('./pages/admin-elearning/admin-elearning.page').then(m => m.AdminElearningPage)
      },
      {
        path: 'elearning/:id',
        loadComponent: () => import('./pages/admin-elearning/elearning-builder/elearning-builder.page').then(m => m.ElearningBuilderPage)
      },

      {
        path: 'enervantage/users',
        loadComponent: () => import('./pages/admin-enervantage/admin-enervantage-users/admin-enervantage-users.page').then(m => m.AdminEnervantageUsersPage)
      },
      {
        path: 'enervantage/processes',
        loadComponent: () => import('./pages/admin-enervantage/processes/processes.page').then(m => m.ProcessesPage)
      },
      {
        path: 'enervantage/processes/:id',
        loadComponent: () => import('./pages/admin-enervantage/processes/process-detail/process-detail.page').then(m => m.ProcessDetailPage)
      },
      {
        path: 'business/users',
        loadComponent: () => import('./pages/admin-business/admin-business-users/admin-business-users.page').then(m => m.AdminBusinessUsersPage)
      },
      {
        path: 'regional/users',
        loadComponent: () => import('./pages/admin-regional/admin-regional-users/admin-regional-users.page').then(m => m.AdminRegionalUsersPage)
      },
      {
        path: 'enervantage/customers',
        loadComponent: () => import('./pages/admin-enervantage/customers/customers.page').then(m => m.CustomersPage)
      },
      {
        path: 'enervantage/customers/:id',
        loadComponent: () => import('./pages/admin-enervantage/customers/customer-detail/customer-detail.page').then(m => m.CustomerDetailPage)
      },
      {
        path: 'enervantage/customers/:customerId/processes',
        loadComponent: () => import('./pages/admin-enervantage/customers/customer-detail/processes/processes.page').then(m => m.ProcessesPage)
      },
      {
        path: 'enervantage/customers/:customerId/processes/:instanceId/view',
        loadComponent: () => import('./pages/admin-enervantage/customers/customer-detail/processes/process-data-viewer/process-data-viewer.page').then(m => m.ProcessDataViewerPage)
      },
      {
        path: 'enervantage/action-plan',
        loadComponent: () => import('./pages/admin-enervantage/action-plan/action-plan.page').then(m => m.ActionPlanPage)
      },
      {
        path: 'enervantage/action-plan-items/new',
        loadComponent: () => import('./pages/admin-enervantage/action-plan/action-items/action-items-detail/action-items-detail.page').then(m => m.ActionItemsDetailPage)
      },
      {
        path: 'enervantage/action-plan-items/:id',
        loadComponent: () => import('./pages/admin-enervantage/action-plan/action-items/action-items-detail/action-items-detail.page').then(m => m.ActionItemsDetailPage),
      },
      {
        path: 'enervantage/leads',
        loadComponent: () => import('./pages/admin-enervantage/leads/leads.page').then(m => m.LeadsPage)
      },
      {
        path: 'enervantage/projects',
        loadComponent: () => import('./pages/admin-enervantage/projects/projects.page').then(m => m.ProjectsPage)
      },
      {
        path: 'enervantage/projects/:customerId',
        loadComponent: () => import('./pages/admin-enervantage/projects/project-detail/project-detail.page').then(m => m.ProjectDetailPage)
      },
      {
        path: 'enervantage/leads/:id',
        loadComponent: () => import('./pages/admin-enervantage/leads/lead-detail/lead-detail.page').then(m => m.LeadDetailPage)
      },
      {
        path: 'ethikrat/applications',
        title: 'Ethikrat Bewerbungen',
        loadComponent: () => import('./pages/ethikrat/applications.page').then(m => m.EthikratApplicationsPage)
      },
      {
        path: 'ethikrat/applications/:id',
        title: 'Bewerbungsdetails',
        loadComponent: () => import('./pages/ethikrat/application-detail/application-detail.page').then(m => m.ApplicationDetailPage)
      }
    ]
  },

  {
    path: 'not-authorized',
    loadComponent: () => import('./not-authorized/not-authorized.page').then(m => m.NotAuthorizedPage)
  },
  {
    path: 'admin/enervantage/customers/:customerId/processes/:templateId',
    loadComponent: () => import('./pages/admin-enervantage/customers/customer-detail/processes/process-stepper/process-stepper.page').then(m => m.ProcessStepperPage)
  },
  {
    path: 'admin/enervantage/customers/:customerId/processes/:instanceId',
    loadComponent: () => import('./pages/admin-enervantage/customers/customer-detail/processes/process-stepper/process-stepper.page').then(m => m.ProcessStepperPage)
  },
  {
    path: 'realizability',
    loadComponent: () => import('./pages/admin-enervantage/customers/customer-detail/realizability/realizability.page').then(m => m.RealizabilityPage)
  },
  {
    path: 'action-plan',
    loadComponent: () => import('./pages/admin-enervantage/customers/customer-detail/action-plan/action-plan.page').then(m => m.ActionPlanPage)
  },
  {
    path: 'tender',
    loadComponent: () => import('./pages/admin-enervantage/customers/customer-detail/tender/tender.page').then(m => m.TenderPage)
  },



];
